import type { TranslateFn } from '@/i18n/useTimePilotT';
import type { ContentModerationFinding } from '@/shared/cors-provenance-api';

type FindingProvenance = Pick<
  ContentModerationFinding,
  'creationMethod' | 'findingStatus' | 'reviewedBy' | 'flaggedBy'
>;

/** Dashboard "User flagged" filter: any MANUAL finding, any status. */
export function isUserFlagged(finding: Pick<ContentModerationFinding, 'creationMethod'>): boolean {
  return finding.creationMethod === 'MANUAL';
}

/**
 * Nav badge: user flags that still need a CSAM Verifier.
 * MANUAL + CONFIRMED + no reviewedBy. System detections and already-reviewed
 * user flags do not count.
 */
export function needsVerifierAttention(finding: FindingProvenance): boolean {
  return (
    finding.creationMethod === 'MANUAL' &&
    finding.findingStatus === 'CONFIRMED' &&
    !finding.reviewedBy
  );
}

/** System FLAGGED plus unverified user flags (MANUAL + CONFIRMED, no reviewedBy). */
export function isAwaitingVerifierReview(finding: FindingProvenance): boolean {
  return finding.findingStatus === 'FLAGGED' || needsVerifierAttention(finding);
}

/** Verified CSAM — excludes user flags that still need a CSAM Verifier. */
export function isVerifiedConfirmed(finding: FindingProvenance): boolean {
  return finding.findingStatus === 'CONFIRMED' && !needsVerifierAttention(finding);
}

/**
 * Split unverified user flags out of the summary CONFIRMED bucket.
 * Returns nulls until both the summary and the attention query are known —
 * do not treat a missing attention count as 0.
 */
export function adjustSummaryForAttention(
  summary: { flaggedCount: number; confirmedCount: number } | undefined,
  attentionCount: number | undefined,
): { needsReviewCount: number | null; confirmedCount: number | null } {
  if (summary === undefined || attentionCount === undefined) {
    return { needsReviewCount: null, confirmedCount: null };
  }
  return {
    needsReviewCount: summary.flaggedCount + attentionCount,
    confirmedCount: Math.max(0, summary.confirmedCount - attentionCount),
  };
}

/**
 * Prefer the known total, but never undercount rows already loaded.
 * A missing attention query used to report 0 and hide later pages.
 */
export function resolvedClientListCount(
  knownCount: number | null | undefined,
  loadedLength: number,
  pagingComplete: boolean,
): number | null {
  if (knownCount != null) return Math.max(knownCount, loadedLength);
  if (!pagingComplete && loadedLength === 0) return null;
  return loadedLength;
}

/**
 * Nav "Reviewed" pill. An empty awaiting-review slice is only trustworthy
 * when the findings query is unfiltered — Cleared loads REVIEWED rows only.
 */
export function isCaseFullyReviewed(input: {
  needsReviewCount: number | null;
  findingsAreUnfiltered: boolean;
  pagingComplete: boolean;
  awaitingReviewCount: number;
}): boolean {
  if (input.needsReviewCount === 0) return true;
  if (input.needsReviewCount != null) return false;
  return (
    input.findingsAreUnfiltered &&
    input.pagingComplete &&
    input.awaitingReviewCount === 0
  );
}

export function countUserFlaggedNeedingAttention(findings: FindingProvenance[]): number {
  return findings.filter(needsVerifierAttention).length;
}

export function provenanceLabel(finding: FindingProvenance, t: TranslateFn): string {
  if (isUserFlagged(finding)) {
    return finding.flaggedBy
      ? t('csam.provenance.flaggedByNamed', { name: finding.flaggedBy })
      : t('csam.provenance.flaggedByUser');
  }
  return t('csam.provenance.systemDetected');
}

export function filterUserFlagged<T extends Pick<ContentModerationFinding, 'creationMethod'>>(
  findings: T[],
  userFlaggedOnly: boolean,
): T[] {
  if (!userFlaggedOnly) return findings;
  return findings.filter(isUserFlagged);
}

export function countUserFlagged<T extends Pick<ContentModerationFinding, 'creationMethod'>>(
  findings: T[],
): number {
  return findings.filter(isUserFlagged).length;
}

export function paginateItems<T>(items: T[], pageIndex: number, pageSize: number): T[] {
  const start = Math.max(0, pageIndex) * pageSize;
  return items.slice(start, start + pageSize);
}
